import React, { forwardRef } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Button,
  IconButton,
  Stack,
  SxProps,
  Theme,
  Typography,
  useTheme
} from '@mui/material';
import { CustomContentProps, SnackbarKey } from 'notistack';

/**
 * Действие внутри уведомления — например «Вернуть» у снятой закладки. Нажатие
 * закрывает уведомление: держать открытым снекбар с уже отменённым действием
 * значило бы предлагать отменить его второй раз.
 */
export interface ICustomAlertAction {
  text: string;
  onClick: () => void;
}

export interface IProps extends CustomContentProps {
  onClose: (id: SnackbarKey) => void;
  icon?: React.ReactNode;
  severity?: 'success' | 'error' | 'warning' | 'info';
  className?: string;
  undoAction?: ICustomAlertAction;
  /** Точечные стили плашки поверх общих (вариант в месте вызова) */
  alertSx?: SxProps<Theme>;
}

/**
 * Компонент для вывода уведомлений пользователю
 * @param root0 - пропсы notistack
 * @param root0.id - id
 * @param root0.message - сообщение
 * @param root0.onClose - ф-ия закрытия уведомления
 * @param root0.icon - опционально, новая иконка
 * @param root0.severity - опционально, тип MUI-Alert уведомления: 'success' | 'error' | 'warning' | 'info'
 * @param root0.className - опционально, классы для MUI-Alert уведомления
 * @param root0.undoAction - опционально, действие уведомления (например «Вернуть»).
 * Имя не `action`: так называется штатный проп notistack под правый слот
 * @param root0.alertSx - опционально, стили плашки поверх общих
 */
export const CustomAlert = forwardRef<HTMLDivElement, IProps>(
  (
    {
      id,
      message,
      onClose,
      icon,
      severity = 'success',
      className = '',
      undoAction,
      alertSx
    }: IProps,
    ref
  ) => {
    const theme = useTheme();

    return (
      <Alert
        severity={severity}
        icon={icon}
        ref={ref}
        action={
          <Stack direction='row' alignItems='center' gap={1}>
            {undoAction && (
              <Button
                color='inherit'
                size='small'
                sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                onClick={() => {
                  undoAction.onClick();
                  onClose(id);
                }}
              >
                {undoAction.text}
              </Button>
            )}
            <IconButton
              aria-label='Закрыть уведомление'
              onClick={() => onClose(id)}
            >
              <CloseIcon sx={{ width: 20, height: 20 }} />
            </IconButton>
          </Stack>
        }
        sx={{
          width: '100%',
          px: '16px',
          // Текст сообщения (16px/20px) и текст кнопки действия (13px/22.75px, жирный)
          // имеют разную высоту строки — при alignItems: 'center' их боксы совпадают
          // по геометрическому центру, но БАЗОВЫЕ ЛИНИИ текста расходятся (разные
          // шрифтовые метрики), и кнопка визуально «подвисает» над строкой сообщения.
          // baseline выравнивает именно текстовые базовые линии, поэтому иконка
          // статуса (без текстовой базовой линии) получает alignSelf: 'center' отдельно
          alignItems: 'baseline',
          '& .MuiAlert-icon': { alignSelf: 'center' },
          '& .MuiAlert-action': { pt: 0 },
          [theme.breakpoints.down('sm')]: { width: '100%' },
          boxShadow:
            '0px 3px 5px -1px rgba(0, 0, 0, 0.1), 0px 6px 10px 0px rgba(0, 0, 0, 0.14)',
          ...(alertSx as object)
        }}
        className={className}
      >
        <Typography
          sx={{ wordWrap: 'break-word' }}
          fontSize={16}
          lineHeight={'20px'}
          fontWeight={500}
        >
          {message}
        </Typography>
      </Alert>
    );
  }
);
